import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  FormControl,
  IconButton,
} from '@weave-mui/material';
import { TrashM } from '@weave-mui/icons-weave';
import CloseIcon from '@mui/icons-material/Close';
import Select from 'react-select';
import NotificationSection from './NotificationSection';

/**
 * TestSuiteReviewForm - Review and finalize test suite creation
 *
 * Displays selected test cases with filtering options and a form to enter
 * test suite details like name, description, and notification preferences.
 *
 * @param {Object} props - Component props
 * @param {Array} props.testCases - Array of selected test case objects
 * @param {string} props.testSuiteName - Test suite name value
 * @param {string} props.description - Test suite description value
 * @param {boolean} props.notifyMe - Whether to notify current user
 * @param {boolean} props.notifyOthers - Whether to notify other users
 * @param {string} props.notificationEmail - Email addresses for notifications
 * @param {boolean} props.updateJiraXray - Whether to update JIRA Xray with status
 * @param {Array} props.selectedTags - Array of selected filter tags (from parent)
 * @param {string} props.environment - Current environment name
 * @param {Function} props.onFieldChange - Callback when a form field changes
 * @param {Function} props.onSubmit - Callback when submitting the form
 * @param {Function} props.onExit - Callback when clicking exit button
 * @param {Function} props.onCancel - Callback when clicking cancel button
 * @param {Function} props.onRemove - Callback when removing a test case
 * @param {boolean} props.isSubmitting - Whether the form is being submitted
 * @param {string} props.userEmail - Current user's email address
 * @param {string} [props.pageType='new'] - Page type: 'new' for create mode, 'edit' for edit mode
 * @returns {JSX.Element} The review form component
 */
function TestSuiteReviewForm({
  testCases,
  testSuiteName,
  description,
  notifyMe,
  notifyOthers,
  notificationEmail,
  updateJiraXray = false,
  selectedTags: initialSelectedTags = [],
  environment = '',
  onFieldChange,
  onSubmit,
  onExit,
  onCancel,
  onRemove,
  isSubmitting,
  userEmail = '',
  pageType = 'new',
}) {
  // Local state for tag filtering
  const [localSelectedTags, setLocalSelectedTags] = useState(() => {
    if (
      !Array.isArray(initialSelectedTags) ||
      initialSelectedTags.length === 0
    ) {
      return [];
    }
    return initialSelectedTags.map((tag) =>
      typeof tag === 'string'
        ? { value: tag, label: tag }
        : { value: tag.value || tag, label: tag.label || tag.value || tag }
    );
  });
  const [filteredTestCases, setFilteredTestCases] = useState([]);

  // Extract unique tags from test cases and combine with suite tags
  const uniqueTags = useMemo(() => {
    const allTags = new Set();

    // Add suite-level tags
    if (Array.isArray(initialSelectedTags) && initialSelectedTags.length > 0) {
      initialSelectedTags.forEach((tag) => {
        const tagValue = typeof tag === 'string' ? tag : tag.value || tag;
        if (tagValue) {
          allTags.add(tagValue);
        }
      });
    }

    // Add tags from test cases
    if (environment && testCases.length > 0) {
      const testDataEnvKey = `testdata_${environment.toLowerCase()}`;
      testCases.forEach((testCase) => {
        const testDataEnv = testCase[testDataEnvKey] || [];
        if (testDataEnv.length > 0) {
          testDataEnv.forEach((item) => {
            if (item._tags) {
              const tags = Array.isArray(item._tags)
                ? item._tags
                : item._tags.split(/[, ]+/);
              tags.forEach((tag) => {
                if (tag && tag !== null && tag !== undefined && tag !== '') {
                  allTags.add(tag);
                }
              });
            }
          });
        }
      });
    }

    return Array.from(allTags).map((tag) => ({ value: tag, label: tag }));
  }, [testCases, environment, initialSelectedTags]);

  // Initialize filtered test cases
  useEffect(() => {
    if (!environment) {
      setFilteredTestCases(testCases.map((tc) => ({ ...tc, filtered: true })));
      return;
    }

    const selectedTagValues = localSelectedTags.map((tag) => tag.value);
    if (selectedTagValues.length === 0) {
      setFilteredTestCases(testCases.map((tc) => ({ ...tc, filtered: true })));
    } else {
      const testDataEnvKey = `testdata_${environment.toLowerCase()}`;
      const filteredData = testCases.map((testCase) => {
        const testDataEnv = testCase[testDataEnvKey];

        // If testdata is not loaded yet, show the test case normally
        if (
          !testDataEnv ||
          !Array.isArray(testDataEnv) ||
          testDataEnv.length === 0
        ) {
          return { ...testCase, filtered: true };
        }

        // Check if any item in testdata has tags that match selected tags
        const isFiltered = testDataEnv.some((item) => {
          if (!item._tags) return false;

          // Handle both array and string formats for _tags
          const tags = Array.isArray(item._tags)
            ? item._tags
            : item._tags.split(/[, ]+/).filter((t) => t);

          // Check if any selected tag matches any of the item's tags
          return selectedTagValues.some((selectedTag) =>
            tags.includes(selectedTag)
          );
        });

        return { ...testCase, filtered: isFiltered };
      });
      setFilteredTestCases(filteredData);
    }
  }, [testCases, localSelectedTags, environment]);

  // Sync with parent selectedTags prop changes
  useEffect(() => {
    // Always sync, even if empty array (to handle clearing tags)
    setLocalSelectedTags(
      Array.isArray(initialSelectedTags)
        ? initialSelectedTags.map((tag) =>
            typeof tag === 'string'
              ? { value: tag, label: tag }
              : {
                  value: tag.value || tag,
                  label: tag.label || tag.value || tag,
                }
          )
        : []
    );
  }, [initialSelectedTags]);

  // Handle tag filter change
  const handleTagFilterChange = (selectedOptions) => {
    const newSelectedTags = selectedOptions || [];
    setLocalSelectedTags(newSelectedTags);
    // Sync tags back to parent component as array of strings
    const tagValues = newSelectedTags.map((tag) => tag.value);
    onFieldChange('selectedTags', tagValues);
  };
  return (
    <Box>
      {/* Selected Tests Section */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Review the selected test cases to create your test suite.
          </Typography>
          <FormControl size="small" sx={{ minWidth: 150, gap: 1 }}>
            <Typography
              variant="caption"
              sx={{ mb: 1, fontWeight: 'medium', color: 'text.secondary' }}
            >
              Filter by tags
            </Typography>
            <Select
              isMulti
              options={uniqueTags}
              value={localSelectedTags}
              onChange={handleTagFilterChange}
              placeholder="Select"
              isClearable
              styles={{
                control: (provided) => ({
                  ...provided,
                  minHeight: 40,
                  height: 40,
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  height: 40,
                  padding: '0 8px',
                }),
                input: (provided) => ({
                  ...provided,
                  margin: '0',
                  padding: '0',
                }),
                indicatorsContainer: (provided) => ({
                  ...provided,
                  height: 40,
                }),
                menuPortal: (provided) => ({
                  ...provided,
                  zIndex: 999999,
                }),
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </FormControl>
        </Box>

        {/* Filter Chips */}
        {localSelectedTags && localSelectedTags.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {localSelectedTags.map((tag) => (
              <Chip
                key={tag.value}
                label={tag.label}
                onDelete={() => {
                  handleTagFilterChange(
                    localSelectedTags.filter((t) => t.value !== tag.value)
                  );
                }}
                size="small"
                deleteIcon={<CloseIcon sx={{ width: 24, height: 24 }} />}
                sx={{
                  minWidth: 70,
                  height: 24,
                  fontSize: '12px',
                  fontWeight: 600,
                  borderRadius: '4px',
                }}
              />
            ))}
          </Box>
        )}

        {/* Test Cases Count */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1, display: 'block' }}
        >
          {testCases.length} test{testCases.length !== 1 ? 's' : ''} selected
        </Typography>

        {/* Test Cases List */}
        <Box
          sx={{
            maxHeight: 200,
            overflow: 'auto',
            borderRadius: 1,
          }}
        >
          {filteredTestCases.map((tc, index) => {
            // filtered: true means it matches the selected tags (show normally)
            // filtered: false means it doesn't match (show dimmed)
            const matchesFilter = tc.filtered === true;
            return (
              <Box
                key={tc.testcaseid || index}
                sx={{
                  mb: 1,
                  px: 2,
                  py: 1.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: matchesFilter ? 'divider' : '#cccccc',
                  backgroundColor: (theme) => theme.palette.action.hover,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  opacity: matchesFilter ? 1 : 0.3,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 1,
                    width: '100%',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    {tc.subcategoryname}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}
                  >
                    {tc.displayname || tc.name}
                  </Typography>
                </Box>
                <IconButton
                  edge="end"
                  onClick={() => onRemove(tc)}
                  size="small"
                >
                  <TrashM color="action" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Test Suite Details Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium' }}>
          Test suite details
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          {/* Left Column */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Test suite name"
              value={testSuiteName}
              onChange={(e) => onFieldChange('testSuiteName', e.target.value)}
              placeholder="Enter test suite name"
              size="small"
              sx={{
                mb: 3, // 24px margin bottom
                '& .MuiInputBase-root': {
                  height: 40,
                },
              }}
            />

            {/* Notification Section */}
            <NotificationSection
              notifyMe={notifyMe}
              notifyOthers={notifyOthers}
              notificationEmail={notificationEmail}
              userEmail={userEmail}
              onNotifyMeChange={(checked) => onFieldChange('notifyMe', checked)}
              onNotifyOthersChange={(checked) =>
                onFieldChange('notifyOthers', checked)
              }
              onNotificationEmailChange={(value) =>
                onFieldChange('notificationEmail', value)
              }
              showJiraSection={true}
              updateJiraXray={updateJiraXray}
              onJiraXrayChange={(checked) =>
                onFieldChange('updateJiraXray', checked)
              }
            />
          </Box>

          {/* Right Column - Description */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Description"
              value={description}
              onChange={(e) => onFieldChange('description', e.target.value)}
              placeholder="Enter a brief description"
              size="small"
              multiline
              rows={12}
              sx={{
                height: '100%',
                '& .MuiInputBase-root': {
                  height: '100%',
                  minHeight: '120px',
                  alignItems: 'flex-start',
                },
                '& .MuiInputBase-input': {
                  height: '100% !important',
                  overflow: 'auto',
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
        <Button variant="outlined" onClick={onExit} disabled={isSubmitting}>
          Exit
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={isSubmitting || !testSuiteName.trim()}
          >
            {isSubmitting
              ? pageType === 'edit'
                ? 'Updating...'
                : 'Creating...'
              : pageType === 'edit'
              ? 'Update test suite'
              : 'Create test suite'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default TestSuiteReviewForm;
